import React from 'react';
import { tokenManager } from '../utils/auth';

/**
 * Counts how many times we've tried to auto-recover from a boot/render crash
 * within this browsing session. Kept in sessionStorage so it survives the
 * reloads we trigger, but resets when the standalone app / tab is fully closed.
 */
export const RECOVERY_KEY = 'cc_boot_recovery';

interface Props {
  children: React.ReactNode;
}

interface State {
  failed: boolean;
}

/**
 * Top-level error boundary.
 *
 * Without this, ANY uncaught exception during boot (e.g. new code reading
 * stale/incompatible persisted auth state from a long-lived home-screen app)
 * unmounts the whole React tree → permanent white screen until the user
 * deletes and re-adds the app.
 *
 * Instead we recover in stages, so a wedged client heals itself:
 *   1st crash  → reload (handles transient errors and stale bundle references)
 *   2nd crash  → clear auth storage + go to /login (handles stale/incompatible
 *                persisted state — the thing that caused the white screen)
 *   3rd+ crash → stop reloading and show a real "something went wrong" screen
 */
export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    const attempts = Number(sessionStorage.getItem(RECOVERY_KEY) || '0');
    // eslint-disable-next-line no-console
    console.error(`CrewCast crashed (recovery attempt ${attempts}):`, error, info);

    if (attempts === 0) {
      sessionStorage.setItem(RECOVERY_KEY, '1');
      window.location.reload();
    } else if (attempts === 1) {
      sessionStorage.setItem(RECOVERY_KEY, '2');
      try {
        tokenManager.clearTokens();
      } catch {
        /* ignore — we're already in a failure path */
      }
      window.location.replace('/login');
    }
    // attempts >= 2: fall through to the fallback UI in render().
  }

  private handleReload = () => {
    sessionStorage.removeItem(RECOVERY_KEY);
    try {
      tokenManager.clearTokens();
    } catch {
      /* ignore */
    }
    window.location.replace('/login');
  };

  render() {
    if (!this.state.failed) return this.props.children;

    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          padding: 24,
          textAlign: 'center',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          color: '#1f2937',
        }}
      >
        <h1 style={{ fontSize: 22, margin: 0 }}>Something went wrong</h1>
        <p style={{ margin: 0, maxWidth: 320, color: '#6b7280' }}>
          We hit an unexpected error. Reloading usually fixes it.
        </p>
        <button
          onClick={this.handleReload}
          style={{
            marginTop: 8,
            padding: '12px 24px',
            fontSize: 16,
            fontWeight: 600,
            color: '#ffffff',
            background: '#00bf62',
            border: 'none',
            borderRadius: 12,
            cursor: 'pointer',
          }}
        >
          Reload
        </button>
      </div>
    );
  }
}
